import { CLASSIFICACOES } from "./constants";

export const SIT = {
  DENTRO: "DENTRO DA TOLERÂNCIA",
  ATENCAO: "ATENÇÃO",
  FORA: "FORA DA TOLERÂNCIA",
  SEM_TOL: "TOLERÂNCIA NÃO INFORMADA",
  INSUF: "DADOS INSUFICIENTES",
};
const EVALUATED = [SIT.DENTRO, SIT.ATENCAO, SIT.FORA];

export function num(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim().replace(/\s/g, "").replace(",", ".");
  if (s === "" || s === "-" || s === "+" || s === ".") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export function sub(a, b) {
  const x = num(a);
  const y = num(b);
  return x === null || y === null ? null : x - y;
}

const round = (n, d = 1) => Math.round(n * 10 ** d) / 10 ** d;
const dec = (n, d = 1) => Math.abs(round(n, d)).toFixed(d).replace(".", ",");

export function fmtSigned(n, d = 1) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  const r = round(n, d);
  return `${r > 0 ? "+" : r < 0 ? "-" : ""}${dec(r, d)} mm`;
}
export const fmtAbs = (n, d = 1) => (n === null || n === undefined ? "—" : `${dec(n, d)} mm`);
export const fmtTol = (t) => (t === null || t === undefined ? SIT.SEM_TOL : `±${dec(t)} mm`);
export const fmtPct = (n) => (n === null || n === undefined ? "—" : `${round(n, 1).toFixed(1).replace(".", ",")}%`);
export function fmtCoord(v) {
  const n = num(v);
  return n === null ? "—" : n.toLocaleString("pt-BR", { maximumFractionDigits: 2 });
}
export function shortSigned(n) {
  const r = round(n, 1);
  const body = Number.isInteger(r) ? String(Math.abs(r)) : dec(r);
  return `${r > 0 ? "+" : r < 0 ? "-" : ""}${body}`;
}

function attnRatio(reference) {
  const p = num(reference?.limiar_atencao);
  return p !== null && p > 0 && p < 100 ? p / 100 : 0.8;
}

export function classify(value, tol, reference) {
  if (value === null || value === undefined) return SIT.INSUF;
  if (tol === null || tol === undefined) return SIT.SEM_TOL;
  if (value > tol) return SIT.FORA;
  if (value > tol * attnRatio(reference)) return SIT.ATENCAO;
  return SIT.DENTRO;
}

const absTol = (v) => (num(v) === null ? null : Math.abs(num(v)));
export const tolSource = (reference) => {
  const src = [reference?.fonte_tolerancia, reference?.fonte_detalhe].filter(Boolean).join(" — ");
  return src || "FONTE NÃO REGISTRADA";
};

export function pointCalc(p, reference = {}) {
  const dx = sub(p.found?.x, p.ref?.x);
  const dy = sub(p.found?.y, p.ref?.y);
  const dz = sub(p.found?.z, p.ref?.z);
  const comps = [dx, dy, dz].filter((v) => v !== null);
  const resultant = comps.length ? Math.sqrt(comps.reduce((s, v) => s + v * v, 0)) : null;
  const diff = sub(p.encontrada, p.nominal);
  const own = absTol(p.tolerancia);
  const tol = own !== null ? own : absTol(reference.tolerancia_padrao);
  const source = tol === null ? null : `${tolSource(reference)}${own !== null ? " (tolerância específica do ponto)" : ""}`;
  const candidates = [resultant, diff === null ? null : Math.abs(diff)].filter((v) => v !== null);
  const governing = candidates.length ? Math.max(...candidates) : null;
  return { dx, dy, dz, resultant, partial: comps.length > 0 && comps.length < 3, diff, tol, tolSource: source, governing, situacao: classify(governing, tol, reference) };
}

export function deviationLabel(numero, c) {
  const parts = [["X", c.dx], ["Y", c.dy], ["Z", c.dz]].filter(([, v]) => v !== null && round(v, 1) !== 0);
  if (!parts.length) return { text: `${numero} - SEM DESVIO`, axes: "" };
  return { text: `${numero} - DESVIO ${parts.map(([, v]) => shortSigned(v)).join("/")} mm`, axes: parts.map(([a]) => `Δ${a}`).join("/") };
}

const OPP = { traseira: "dianteira", dianteira: "traseira", esquerda: "direita", direita: "esquerda" };
export function describeDirection(dx, dy, dz, reference = {}) {
  const parts = [];
  const side = (v, pos, axis) => (pos ? `para a ${v > 0 ? pos : OPP[pos]}` : `sentido ${v > 0 ? "+" : "-"}${axis}`);
  if (dx !== null && round(dx, 1) !== 0) parts.push(`Longitudinal ${fmtSigned(dx)} (${side(dx, reference.x_positivo, "X")})`);
  if (dy !== null && round(dy, 1) !== 0) parts.push(`Transversal ${fmtSigned(dy)} (${side(dy, reference.y_positivo, "Y")})`);
  if (dz !== null && round(dz, 1) !== 0) parts.push(`Vertical ${fmtSigned(dz)} (${dz > 0 ? "subida" : "descida"})`);
  return parts.join(" · ");
}

export function axleCalc(a, reference = {}) {
  const diffLR = sub(a.esquerda, a.direita);
  const desl = num(a.deslocamento);
  const vals = [diffLR, desl].filter((v) => v !== null).map(Math.abs);
  const governing = vals.length ? Math.max(...vals) : null;
  const tol = absTol(reference.tolerancia_eixo);
  return { diffLR, desl, governing, tol, situacao: classify(governing, tol, reference), hasData: [a.esquerda, a.centro, a.direita, a.deslocamento].some((v) => num(v) !== null) };
}

export function diagonalCalc(list = [], reference = {}) {
  const tol = absTol(reference.tolerancia_diagonal);
  const rows = list.map((d) => {
    const diff = sub(d.medida, d.referencia);
    return { ...d, diff, situacao: classify(diff === null ? null : Math.abs(diff), tol, reference) };
  });
  const pairs = [];
  for (let i = 0; i + 1 < list.length; i += 2) {
    const cross = sub(list[i].medida, list[i + 1].medida);
    pairs.push({ a: list[i], b: list[i + 1], cross, situacao: classify(cross === null ? null : Math.abs(cross), tol, reference) });
  }
  return { rows, pairs, tol };
}

export function railCalc(side = [], tolRaw, reference = {}) {
  const vals = side.map((p) => num(p.desvio)).filter((v) => v !== null).map(Math.abs);
  const max = vals.length ? Math.max(...vals) : null;
  const mean = vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
  const tol = absTol(tolRaw);
  return { max, mean, n: vals.length, tol, situacao: classify(max, tol, reference) };
}

export function parallelCalc(par = {}, reference = {}) {
  const diff = sub(par.medicao, par.referencia);
  const tol = absTol(par.tolerancia) ?? absTol(reference.tolerancia_paralelismo);
  return { diff, tol, situacao: classify(diff === null ? null : Math.abs(diff), tol, reference) };
}

export function anomalyCalc(a) {
  const dx = sub(a.found?.x, a.orig?.x);
  const dy = sub(a.found?.y, a.orig?.y);
  const dz = sub(a.found?.z, a.orig?.z);
  const comps = [dx, dy, dz].filter((v) => v !== null);
  return { dx, dy, dz, total: comps.length ? Math.sqrt(comps.reduce((s, v) => s + v * v, 0)) : null };
}

export function torsionPairs(rows) {
  const valid = (r, lado) => r.p.lado === lado && r.c.dz !== null && num(r.p.ref?.x) !== null;
  const D = rows.filter((r) => valid(r, "D"));
  return rows.filter((r) => valid(r, "E")).flatMap((e) => {
    const d = D.find((r) => Math.abs(num(r.p.ref.x) - num(e.p.ref.x)) <= 150);
    if (!d) return [];
    const rel = e.c.dz - d.c.dz;
    const tol = e.c.tol ?? d.c.tol;
    return [{ a: e.p, b: d.p, rel, tol, exceeds: tol !== null && Math.abs(rel) > tol }];
  });
}

const loc = (p) => [p.descricao, p.localizacao].filter(Boolean).join(" — ");
const tag = (p) => `${p.numero}${loc(p) ? ` (${loc(p)})` : ""}`;

export function analyze(insp) {
  const reference = insp.reference || {};
  const anomalies = insp.anomalies || [];
  const rows = (insp.points || []).map((p) => ({ p, c: pointCalc(p, reference) }));
  const by = (s) => rows.filter((r) => r.c.situacao === s);
  const measured = rows.filter((r) => r.c.governing !== null);
  const fora = by(SIT.FORA);
  const atencao = by(SIT.ATENCAO);
  const semTol = by(SIT.SEM_TOL);
  const insuf = by(SIT.INSUF);
  const findings = [];
  const warnings = [];
  let evaluable = rows.filter((r) => EVALUATED.includes(r.c.situacao)).length;

  let maior = null;
  measured.forEach((r) => {
    if (!maior || r.c.governing > maior.valor) maior = { valor: r.c.governing, ponto: r.p.numero, local: loc(r.p) || "—" };
  });
  const media = measured.length ? measured.reduce((s, r) => s + r.c.governing, 0) / measured.length : null;
  const evaluatedRows = rows.filter((r) => EVALUATED.includes(r.c.situacao));
  const conformidade = evaluatedRows.length ? (evaluatedRows.filter((r) => r.c.situacao !== SIT.FORA).length / evaluatedRows.length) * 100 : null;

  fora.forEach((r) => findings.push({ nivel: "fora", texto: `${tag(r.p)}: desvio de ${fmtAbs(r.c.governing)} excede a tolerância de ${fmtTol(r.c.tol)} (fonte: ${r.c.tolSource}).` }));
  atencao.forEach((r) => findings.push({ nivel: "atencao", texto: `${tag(r.p)}: desvio de ${fmtAbs(r.c.governing)} próximo ao limite da tolerância de ${fmtTol(r.c.tol)}.` }));
  rows.filter((r) => r.c.dy !== null && r.c.tol !== null && Math.abs(r.c.dy) > r.c.tol).forEach((r) => findings.push({ nivel: "fora", texto: `Possível deslocamento lateral em ${r.p.numero}: ΔY ${fmtSigned(r.c.dy)} ${describeDirection(null, r.c.dy, null, reference) ? `— ${describeDirection(null, r.c.dy, null, reference)}` : ""}.` }));
  const torsions = torsionPairs(rows);
  torsions.filter((t) => t.exceeds).forEach((t) => findings.push({ nivel: "fora", texto: `Possível torção entre ${t.a.numero} (esq.) e ${t.b.numero} (dir.): diferença vertical relativa de ${fmtSigned(t.rel)} para tolerância de ${fmtTol(t.tol)}.` }));

  (insp.axles || []).forEach((a) => {
    const c = axleCalc(a, reference);
    if (!c.hasData || c.governing === null) return;
    if (EVALUATED.includes(c.situacao)) evaluable += 1;
    if (c.situacao === SIT.FORA) findings.push({ nivel: "fora", texto: `${a.eixo}: diferença esquerda/direita ${fmtSigned(c.diffLR)}${c.desl !== null ? `, deslocamento ${fmtSigned(c.desl)}` : ""} excede a tolerância de eixo ${fmtTol(c.tol)} — possível desalinhamento de eixo.` });
    else if (c.situacao === SIT.ATENCAO) findings.push({ nivel: "atencao", texto: `${a.eixo}: diferença esquerda/direita ${fmtSigned(c.diffLR)} próxima ao limite (${fmtTol(c.tol)}).` });
    else if (c.situacao === SIT.SEM_TOL) warnings.push(`${a.eixo}: medições registradas, porém TOLERÂNCIA NÃO INFORMADA para eixos.`);
  });

  const diag = diagonalCalc(insp.diagonals, reference);
  diag.pairs.forEach((pr) => {
    if (pr.cross === null) return;
    if (EVALUATED.includes(pr.situacao)) evaluable += 1;
    if (pr.situacao === SIT.FORA) findings.push({ nivel: "fora", texto: `Diferença entre diagonais ${pr.a.nome} × ${pr.b.nome}: ${fmtSigned(pr.cross)} (tolerância ${fmtTol(diag.tol)}) — indica possível deslocamento lateral (paralelogramo) ou torção.` });
    else if (pr.situacao === SIT.ATENCAO) findings.push({ nivel: "atencao", texto: `Diferença entre diagonais ${pr.a.nome} × ${pr.b.nome}: ${fmtSigned(pr.cross)} próxima ao limite (${fmtTol(diag.tol)}).` });
    else if (pr.situacao === SIT.SEM_TOL) warnings.push(`Diagonais ${pr.a.nome} × ${pr.b.nome}: diferença de ${fmtSigned(pr.cross)} registrada, porém TOLERÂNCIA NÃO INFORMADA.`);
  });
  diag.rows.filter((d) => d.situacao === SIT.FORA).forEach((d) => findings.push({ nivel: "fora", texto: `Diagonal ${d.nome} difere da referência em ${fmtSigned(d.diff)} (tolerância ${fmtTol(diag.tol)}).` }));

  const rails = insp.rails || {};
  [["esquerdo", "Longarina esquerda"], ["direito", "Longarina direita"]].forEach(([k, label]) => {
    const c = railCalc(rails[k], reference.tolerancia_longarina, reference);
    if (!c.n) return;
    if (EVALUATED.includes(c.situacao)) evaluable += 1;
    if (c.situacao === SIT.FORA) findings.push({ nivel: "fora", texto: `${label}: desvio máximo ${fmtAbs(c.max)} (médio ${fmtAbs(c.mean)}) excede a tolerância ${fmtTol(c.tol)} — possível empenamento.` });
    else if (c.situacao === SIT.SEM_TOL) warnings.push(`${label}: desvios registrados, porém TOLERÂNCIA NÃO INFORMADA.`);
  });
  const par = parallelCalc(rails.paralelismo, reference);
  if (par.diff !== null) {
    if (EVALUATED.includes(par.situacao)) evaluable += 1;
    if (par.situacao === SIT.FORA) findings.push({ nivel: "fora", texto: `Paralelismo das longarinas: diferença de ${fmtSigned(par.diff)} excede a tolerância ${fmtTol(par.tol)} — longarinas fora de paralelismo.` });
    else if (par.situacao === SIT.SEM_TOL) warnings.push("Paralelismo das longarinas: TOLERÂNCIA NÃO INFORMADA.");
  }

  const gravCount = anomalies.reduce((acc, a) => ({ ...acc, [a.gravidade || "SEM GRAVIDADE"]: (acc[a.gravidade || "SEM GRAVIDADE"] || 0) + 1 }), {});
  const tipoCount = anomalies.reduce((acc, a) => (a.tipo ? { ...acc, [a.tipo]: (acc[a.tipo] || 0) + 1 } : acc), {});
  const tipoPrincipal = Object.entries(tipoCount).sort((a, b) => b[1] - a[1])[0]?.[0] || (anomalies.length ? "Não classificado" : "Nenhuma anomalia registrada");
  if (anomalies.length) findings.push({ nivel: "info", texto: `${anomalies.length} anomalia(s) registrada(s): ${Object.entries(gravCount).map(([g, n]) => `${n} ${g}`).join(", ")}.` });
  const visualAnom = (insp.visual || []).filter((v) => v.status === "ANOMALIA").map((v) => v.item);
  if (visualAnom.length) findings.push({ nivel: "info", texto: `Inspeção visual indicou: ${visualAnom.join(", ")}.` });

  if (semTol.length) warnings.push(`TOLERÂNCIA NÃO INFORMADA para: ${semTol.map((r) => r.p.numero).join(", ")} — esses pontos não foram classificados.`);
  if (insuf.length) warnings.push(`Dados insuficientes em: ${insuf.map((r) => r.p.numero).join(", ")} — solicitar nova medição.`);
  if (evaluable && !reference.fonte_tolerancia) warnings.push("Fonte da tolerância não registrada (fabricante, norma técnica ou responsável técnico).");
  const severe = fora.filter((r) => r.c.governing > 2 * r.c.tol);
  if (severe.length === 1 && fora.length === 1) warnings.push(`Desvio elevado em medição isolada (${severe[0].p.numero}) — recomenda-se nova medição para confirmação antes de diagnóstico definitivo.`);

  const otherData = (insp.axles || []).some((a) => axleCalc(a, reference).hasData) || diag.pairs.some((p) => p.cross !== null) || railCalc(rails.esquerdo).n + railCalc(rails.direito).n > 0 || par.diff !== null;
  const dadosSuficientes = measured.length > 0 || otherData;
  const structuralFora = findings.some((f) => f.nivel === "fora");
  const critica = anomalies.some((a) => a.gravidade === "CRÍTICA");
  const significativa = anomalies.some((a) => a.gravidade === "SIGNIFICATIVA");

  let classificacao = null;
  let statusMsg = "";
  if (!dadosSuficientes) {
    statusMsg = "DADOS INSUFICIENTES — SOLICITAR NOVA MEDIÇÃO";
    warnings.push("Não há medições suficientes para emitir diagnóstico estrutural. Solicitar nova medição.");
  } else if (evaluable === 0) {
    statusMsg = "SEM CLASSIFICAÇÃO — TOLERÂNCIA NÃO INFORMADA";
  } else if ((severe.length >= 1 && fora.length >= 2) || (critica && structuralFora)) {
    [, , , classificacao] = CLASSIFICACOES;
  } else if (structuralFora || significativa || critica) {
    [, , classificacao] = CLASSIFICACOES;
  } else if (atencao.length || anomalies.length || semTol.length || insuf.length || visualAnom.length || findings.some((f) => f.nivel === "atencao") || warnings.length) {
    [, classificacao] = CLASSIFICACOES;
  } else {
    [classificacao] = CLASSIFICACOES;
  }

  return {
    classificacao,
    statusMsg,
    dadosSuficientes,
    counts: { total: rows.length, medidos: measured.length, fora: fora.length, atencao: atencao.length, dentro: by(SIT.DENTRO).length, semTol: semTol.length, insuf: insuf.length, anomalias: anomalies.length, avaliados: evaluable },
    maior,
    media,
    conformidade,
    tipoPrincipal,
    findings,
    warnings,
    torsions: torsions.map((t) => ({ a: t.a.numero, b: t.b.numero, rel: t.rel, exceeds: t.exceeds })),
    pontos: rows.map(({ p, c }) => ({ numero: p.numero, descricao: p.descricao, localizacao: p.localizacao, dx: c.dx, dy: c.dy, dz: c.dz, resultant: c.resultant, diff: c.diff, tol: c.tol, situacao: c.situacao })),
    fontes: tolSource(reference),
  };
}
